<template>
  <v-dialog v-model="show" max-width="600" :persistent="loading">
    <v-card>
      <v-card-title>
        {{ title }}
        <v-spacer />
        <v-btn icon @click="$emit('close')" v-if="!loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-container v-if="loading" fluid>
          <v-row align="center" justify="center">
            <v-col class="text-center">
              <v-progress-circular indeterminate color="primary" size="40" />
              <div class="mt-2">{{ loadingMessage }}</div>
            </v-col>
          </v-row>
        </v-container>
        <template v-else>
          <!-- Self-invoice result fields -->
          <div v-if="result?.invoicesCreated !== undefined">
            <strong>Self-Invoices Created:</strong>
            <span
              :class="
                result.invoicesCreated > 0 ? 'success--text' : 'error--text'
              "
            >
              {{ result.invoicesCreated }}
            </span>
          </div>

          <!-- Legacy invoice created field -->
          <div v-if="result?.invoiceCreated !== undefined">
            <strong>Invoice Created:</strong>
            <span
              :class="result?.invoiceCreated ? 'success--text' : 'error--text'"
            >
              {{ result?.invoiceCreated ? "Yes" : "No" }}
            </span>
          </div>

          <div v-if="result?.exceptionMessage">
            <strong>Error:</strong>
            <span class="error--text">{{ result.exceptionMessage }}</span>
          </div>

          <!-- Self-invoice specific: Errors -->
          <div v-if="result?.errors && result.errors.length">
            <strong>Errors:</strong>
            <div style="max-height: 200px; overflow-y: auto">
              <v-list dense>
                <v-list-item v-for="(err, idx) in result.errors" :key="idx">
                  <v-list-item-content>
                    <span class="error--text">
                      Row {{ err.row }}: {{ err.message }}
                    </span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>

          <!-- Self-invoice specific: Missing PDFs -->
          <div v-if="result?.missingPdfs && result.missingPdfs.length">
            <strong>Missing PDFs:</strong>
            <div style="max-height: 200px; overflow-y: auto">
              <v-list dense>
                <v-list-item
                  v-for="(pdf, idx) in result.missingPdfs"
                  :key="idx"
                >
                  <v-list-item-content>
                    <span class="warning--text">{{ pdf }}</span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>

          <!-- Self-invoice specific: Entities Created -->
          <div v-if="result?.entitiesCreated && result.entitiesCreated.length">
            <strong>Entities Created:</strong>
            <div style="max-height: 200px; overflow-y: auto">
              <v-list dense>
                <v-list-item
                  v-for="(entity, idx) in result.entitiesCreated"
                  :key="idx"
                >
                  <v-list-item-content>
                    <span class="success--text">{{ entity }}</span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>
          <div v-if="result?.productsCreated && result.productsCreated.length">
            <strong>Products Created:</strong>
            <div style="max-height: 200px; overflow-y: auto">
              <v-list dense>
                <v-list-item
                  v-for="(prod, idx) in result.productsCreated"
                  :key="idx"
                >
                  <v-list-item-content>{{ prod }}</v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>
          <div v-if="result?.productsErrors && result.productsErrors.length">
            <strong>Products Errors:</strong>
            <div style="max-height: 200px; overflow-y: auto">
              <v-list dense>
                <v-list-item
                  v-for="(err, idx) in result.productsErrors"
                  :key="idx"
                >
                  <v-list-item-content>
                    <span class="error--text">
                      {{ err.product || err.Product }}:
                      {{ err.exceptionMessage }}
                    </span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>

          <div v-if="result?.ordersNotFound && result.ordersNotFound.length">
            <strong>Orders not found in LicenceManager:</strong>
            <div style="max-height: 200px; overflow-y: auto">
              <v-list dense>
                <v-list-item
                  v-for="(order, idx) in result.ordersNotFound"
                  :key="idx"
                >
                  <v-list-item-content>
                    <span>
                      {{ order.orderNumber }} - {{ order.description }} -
                      {{ order.orderDate | date }}
                    </span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>

          <div v-if="result" class="mt-4">
            <strong>JSON Result:</strong>
            <pre class="invoice-result-dialog__json">{{
              formattedResultJson
            }}</pre>
          </div>
        </template>
      </v-card-text>
      <v-card-actions v-if="!loading">
        <v-spacer />
        <v-btn color="primary" text @click="downloadJson" :disabled="!result"
          >Save as JSON</v-btn
        >
        <v-btn color="primary" text @click="$emit('close')">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "InvoiceResultDialog",
  props: {
    value: { type: Boolean, default: false },
    result: { type: [Object, Array, String, Number, Boolean], default: null },
    loading: { type: Boolean, default: false },
    title: { type: String, default: "Invoice Creation Result" },
    loadingMessage: { type: String, default: "Processing invoice..." },
  },
  computed: {
    formattedResultJson() {
      if (this.result === null || this.result === undefined) {
        return "";
      }

      return JSON.stringify(this.result, null, 2);
    },
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  methods: {
    downloadJson() {
      if (!this.result) return;
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
      const filename = `fatture_in_cloud_result_${dateStr}.json`;

      const dataStr = JSON.stringify(this.result, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.invoice-result-dialog__json {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.45;
  margin: 8px 0 0;
  max-height: 360px;
  overflow: auto;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
